import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useNavigate } from "react-router-dom";

type PrintAllContextType = {
  documents: Record<string, boolean>;
  setDocuments: Dispatch<SetStateAction<Record<string, boolean>>>;
  documentPrintingState?: {
    status: "loading" | "error" | "ready";
    reportId: number;
    numDocuments?: number;
  }[];
  verifications?: any;
  setPrintingConditions: Dispatch<SetStateAction<Record<string, boolean>>>;
  setDocumentPrintingState: Dispatch<
    SetStateAction<
      | {
          status: "loading" | "error" | "ready";
          reportId: number;
          numDocuments?: number;
        }[]
      | undefined
    >
  >;
  printVerificationsState: Record<string, boolean>;
  setPrintVerificationsState: Dispatch<SetStateAction<Record<string, boolean>>>;
  timedOut: boolean;
  setTimedOut: Dispatch<SetStateAction<boolean>>;
};

export const PrintAllContext = createContext<PrintAllContextType>({
  documents: {},
  setDocuments: () => {},
  documentPrintingState: undefined,
  setDocumentPrintingState: () => {},
  printVerificationsState: {},
  setPrintingConditions: () => {},
  setPrintVerificationsState: () => {},
  timedOut: false,
  setTimedOut: () => {}
});

export function PrintProvider({ children }: { children: ReactNode }) {
  const [timedOut, setTimedOut] = useState(false);

  const [printingConditions, setPrintingConditions] = useState<
    Record<string, boolean>
  >({});
  const printExecuted = useRef(false);
  // Responsible for ensuring all documents are loaded in Success state
  const [printVerificationsState, setPrintVerificationsState] = useState({});
  const verificationsLoaded = Object.values(printVerificationsState).every(
    (state) => state
  );

  const printConditionsMet = Object.values(printingConditions).every(
    (el) => el
  );

  const [documents, setDocuments] = useState<Record<string, boolean>>({});
  const [documentPrintingState, setDocumentPrintingState] = useState<
    | {
        status: "loading" | "error" | "ready";
        reportId: number;
        numDocuments?: number;
      }[]
    | undefined
  >(undefined);

  const navigate = useNavigate();

  useEffect(() => {
    const listner = () => {
      setDocumentPrintingState(undefined);
      if (window.location.pathname.includes("/print"))
        navigate(window.location.pathname.replace("/print", ""));
      else window.location.reload();
    };
    window.addEventListener("afterprint", listner);

    return () => {
      window.removeEventListener("afterprint", listner);
    };
  }, []);

  useEffect(() => {
    if (
      (printConditionsMet || timedOut) &&
      !printExecuted.current &&
      verificationsLoaded &&
      Object.keys(printVerificationsState).length &&
      documentPrintingState?.every((el) => el.status === "ready") &&
      Boolean(Object.values(documents).every((el) => el))
    ) {
      const expectedDocs = documentPrintingState.reduce((acc, curr) => {
        return (acc += curr.numDocuments || 0);
      }, 0);

      if (expectedDocs === 0) {
        printExecuted.current = true;
        window.print();
      } else {
        const uniqDoc = Object.values(
          Object.keys(documents).reduce(
            (acc, curr) => {
              const key = curr.split("_")[2];

              return { ...acc, [key]: acc[key] ? acc[key] : 1 };
            },
            {} as Record<string, number>
          )
        ).reduce((acc, curr) => {
          return (acc += curr);
        }, 0);

        if (uniqDoc >= expectedDocs) {
          printExecuted.current = true;
          window.print();
        }
      }
    }
  }, [
    documents,
    documentPrintingState,
    verificationsLoaded,
    printConditionsMet
  ]);

  useEffect(() => {
    // on afterprint clear #printable_documents_root
    return () => {
      const root = document.getElementById("printable_documents_root");
      if (root) root.innerHTML = "";
    };
  }, []);

  return (
    <PrintAllContext.Provider
      value={{
        setDocuments,
        documents,
        setPrintingConditions,
        documentPrintingState,
        setDocumentPrintingState,
        printVerificationsState,
        setPrintVerificationsState,
        timedOut,
        setTimedOut
      }}
    >
      {children}
    </PrintAllContext.Provider>
  );
}

export const PrintReady = ({
  verificationId
}: {
  verificationId: number | string;
}) => {
  const {
    setPrintVerificationsState,
    printVerificationsState,
    documentPrintingState
  } = useContext(PrintAllContext);

  useEffect(() => {
    if (
      verificationId in printVerificationsState &&
      !printVerificationsState[verificationId]
    )
      setPrintVerificationsState((prev) => ({
        ...prev,
        [verificationId]: true
      }));
  }, [verificationId, printVerificationsState, documentPrintingState]);

  return null;
};
